// Hotbar tooltips: what an ability actually does, in plain words, read from the same
// `fk_grammar::AbilityDef` the sim runs (effects, area, range, slot, cooldown, cost) so the copy
// can never drift from the rules. The status glosses follow 04 §3.2.
import type { ActionDef, WasmAbility } from './actions';

type Obj = Record<string, unknown>;
const variant = (v: unknown): string => (typeof v === 'string' ? v : v && typeof v === 'object' ? Object.keys(v as object)[0] ?? '' : '');
const payload = (v: unknown): Obj => (v && typeof v === 'object' ? ((v as Obj)[variant(v)] as Obj) ?? {} : {});
const num = (v: unknown): number => Number(v ?? 0);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** What each condition does (04 §3.2), by magnitude. */
export function statusGloss(kind: string, magnitude: number): string {
  switch (kind) {
    case 'Guarded': return `+${magnitude} guard (each hit deals ${magnitude} less)`;
    case 'Warded': return `a ward that soaks the next ${magnitude} damage`;
    case 'Blessed': return `+${magnitude} to weapon and spell damage`;
    case 'Marked': return `takes +${magnitude} damage from every hit, and foes go for it`;
    case 'Hidden': return 'foes cannot pick it as a target until it acts (moving keeps it hidden)';
    case 'Slowed': return `moves ${magnitude} fewer squares`;
    case 'Rooted': return 'cannot move';
    case 'Burning': return `takes ${magnitude} damage at the end of each turn`;
    case 'Weakened': return `−${magnitude} to weapon and spell damage`;
    case 'Staggered': return 'cannot move this turn';
    case 'Stuck': return 'cannot leave its square';
    case 'Bleeding': return 'takes 1 damage at the end of each turn';
    case 'Frenzied': return `grief: deals +${magnitude} damage and takes +${magnitude} from foes for the rest of the fight`;
    // docs/66 RM10, RM9
    case 'Arriving': return 'arrived this turn, acts next turn (it can be seen and struck)';
    case 'Raised': return 'raised once from the fallen; it cannot rise again';
    case 'Captive': return 'held by the foe: no foe strikes them until a hero is within 3 squares';
    // docs/69: the two the sim sets itself
    case 'Downed': return 'at 0 HP and cannot act; a friend beside it can Stabilise it';
    case 'Injured': return 'hurt in an earlier fight; it carries the wound until it rests';
    // docs/104 PD2: the magnitude names the paladin, so it says nothing of a number
    case 'Shielded': return 'the next foe’s blow on it this turn lands on its paladin instead, 1 lighter, while the paladin stands within 3 squares';
    // docs/93 AQ8, AQ7
    case 'Floundering': return 'out of its depth in deep water: takes 2 damage each turn (4 if laden; armour is no help), cannot attack, cast or use items, and can only take one step out or hold; a friend beside it can Lend a Hand';
    case 'Surfaced': return 'came up out of the deep to strike: seen and open to blows until it dives again';
    default: return kind.toLowerCase();
  }
}

function dmgText(d: unknown): string {
  const k = variant(d), v = num((d as Obj | undefined)?.[k]);
  switch (k) {
    case 'Weapon': return 'weapon damage';
    case 'WeaponPlus': return v < 0 ? `weapon damage −${-v}` : `weapon damage +${v}`;
    case 'Spell': return `${v} spell damage`;
    case 'Flat': return `${v} damage`;
    default: return 'damage';
  }
}

function whoText(to: string, area: string): string {
  const many = area !== 'Single' && area !== 'SelfUnit';
  // a song's or an aura's reach round you catches no one: it is everyone within it
  const caught = area === 'AlliesWithin' ? '' : ' caught';
  switch (to) {
    case 'Enemies': return many ? `every foe${caught}` : 'a foe';
    case 'Allies': return many ? `every ally${caught}` : 'an ally';
    case 'All': return 'everyone caught (friend or foe)';
    case 'Caster': return 'yourself';
    default: return 'the square';
  }
}

function areaText(a: WasmAbility): string {
  const area = a.area;
  const k = variant(area);
  const max = a.range.max;
  const reach = typeof max === 'object' ? `up to ${plural(max.Tiles, 'square')} away` : max === 'Reach' ? 'next to you' : max === 'Weapon' ? 'within your weapon’s range' : '';
  switch (k) {
    case 'Single': return reach;
    case 'SelfUnit': return '';
    case 'Adjacent': return 'all around you';
    case 'Blast': { const r = num(payload(area)['radius']); return `in a ${2 * r + 1}×${2 * r + 1} burst ${reach}`.trim(); }
    case 'Line': return `in a line ${plural(num(payload(area)['len']), 'square')} long`;
    case 'AlliesWithin': return `within ${plural(num((area as Obj)['AlliesWithin']), 'square')} of you`;
    default: return '';
  }
}

function requirementText(r: unknown): string {
  const k = variant(r), p = (r as Obj | undefined)?.[k];
  switch (k) {
    case 'SelfHidden': return 'while you are hidden';
    case 'TargetHas': return `against a ${String(p).toLowerCase()} target`;
    case 'TargetHasAny2': { const [a, b] = (p as string[]) ?? []; return `against a ${String(a).toLowerCase()} or ${String(b).toLowerCase()} target`; }
    case 'AllyAdjacentToTarget': return 'when an ally stands next to the target';
    case 'MainHand': return `with a ${String(p).toLowerCase()} in hand`;
    case 'OffHand': return `with a ${String(p).toLowerCase()} in your off hand`;
    case 'TargetDowned': return 'on a downed ally';
    default: return '';
  }
}

/** docs/104 BD3: the songs (`AbilityId::is_song`). */
const SONG_IDS: ReadonlySet<number> = new Set([220, 221, 224, 225, 227, 190, 191, 192, 193, 194]);
/** docs/104: what the rules add to an ability beyond its effects. */
const SPECIAL: Readonly<Record<number, string>> = {
  200: 'Paid in Zeal, which only blows on you or beside you give.',
  212: 'Guarded 3 instead on a friend in or beside a thicket.',
  216: '+2 damage under rain or storm, outdoors.',
  187: '+2 damage under rain or storm, outdoors.',
};

/** The ability's effect, one sentence per clause ("Deals weapon damage to a foe next to you. Pushes it 1 square."). */
export function describeAbility(a: WasmAbility): string {
  switch (a.id) {
    case 1: return 'Walk to a square you can reach this turn. Rough ground costs more; foes next to you hold you up.';
    case 2: return 'Do nothing with this slot (the hero stands ready).';
    case 3: return 'Work something next to you: open a chest, pull a lever, read an inscription.';
    case 6: return 'Use something from your bag (a potion, a scroll); pick it in the bag.';
    // docs/59: passives the rules read where they apply (they have no effects of their own)
    case 55: return 'Always on. +1 range and your shots ignore 1 guard.';
    case 110: return 'Always on. Your melee reach is 2: strike a foe two squares away.';
    case 111: return 'Always on. When your blow drops a foe you heal 3 HP and regain 1 Stamina.';
    case 112: return 'Always on. When a foe hits you in melee and you are still standing, you strike back for 2 damage.';
    case 120: return 'Always on. You ignore zones of control: no cost to break away, no stopping beside a foe.';
    case 121: return 'Always on. Hidden things within 2 squares of you show themselves, and a disguised trap there is seen for what it is.';
    case 122: return 'Always on. After a won fight you skim 15% of the haul’s gold before the spoils are split.';
    // docs/103: the rogue's class ability and the party's half of it
    // docs/93 AQ8: Lend a Hand (its haul is the sim's `bank_for`, not a plain pull)
    case 9: return 'Haul a friend floundering in deep water beside you out onto the nearest free ground they can stand on. Only for a friend who is floundering.';
    // docs/155 WB1: Break Free
    case 17: return 'Tear yourself, or a friend beside you, free of a web: no longer rooted or stuck, and the web under them is cut away. Only while someone is caught.';
    case 28: return 'Always on. When a friend touches the foe on the side opposite you, your weapon blow on it deals double: Backstab! A friend who steps round in the same action counts.';
    case 123: return 'Always on. A foe you touch is watching you: a friend’s weapon blow on it from your far side deals +2 damage.';
    // docs/104: the three later classes' always-on abilities, and the oaths' auras
    case 201: return 'A friend within 3 squares is Shielded for the turn: the next foe’s blow on them lands on you instead, 1 lighter, while you stand within 3 squares of them (your guard and wards take it). Not on yourself; a hazard, a status or a trap is nobody’s to take.';
    case 202: return 'Always on. Every foe’s blow that lands on you, or on a friend beside you, gives you 1 Zeal. Zeal never refills on its own: stand in the thick of it.';
    case 205: return 'Always on. Your oath’s aura reaches every friend or foe within two squares, not only those beside you.';
    case 213: return 'Always on. You walk thicket and mud at one pace, and Verdance refills 1 more at the end of a turn you stand in or beside thicket, shallows or mud.';
    case 222: return 'Always on. Every song you play adds 1 Tempo. Tempo never refills on its own: keep playing to build to a Finale.';
    case 180: return 'Always on (Aura of Protection). As each turn ends, every friend beside you is Guarded 1 for the next turn: each hit on them deals 1 less.';
    case 181: return 'Always on (Vow of Enmity). A foe whose blow lands on you or a friend beside you is Marked until the end of the next turn: every blow on it deals 1 more.';
    case 182: return 'Always on (Aura of Renewal). As each turn ends, every hurt friend beside you heals 1.';
    case 183: return 'Always on (Aura of Dread). As each turn ends, every foe beside you is Weakened 1 for the next turn: its blows and spells deal 1 less.';
  }
  const area = variant(a.area);
  const where = areaText(a);
  // docs/63 RF7: a teleport's gift to friends reaches those beside where you land, not you
  const lands = a.effects.some((e) => variant(e) === 'Teleport');
  const whom = (to: string) => (lands && to === 'Allies' && area === 'Single' ? 'each friend beside where you land' : whoText(to, area));
  const parts: string[] = [];
  for (const e of a.effects) {
    const p = payload(e);
    switch (variant(e)) {
      case 'Damage': parts.push(`Deals ${dmgText(p['dmg'])} to ${whoText(variant(p['to']), area)}${where ? ` ${where}` : ''}.`); break;
      case 'BonusDamageIf': parts.push(`+${num(p['amount'])} damage ${requirementText(p['req'])}.`); break;
      case 'DamageMul': parts.push(`Weapon damage ×${num(p['num'])}.`); break;
      case 'Heal': parts.push(`Heals ${whom(variant(p['to']))} for ${num(p['amount'])}${where && variant(p['to']) !== 'Caster' ? ` ${where}` : ''}.`); break;
      case 'Push': parts.push(`Pushes the target ${plural(num(p['tiles']), 'square')} away.`); break;
      case 'Pull': parts.push(`Pulls the target ${plural(num(p['tiles']), 'square')} closer.`); break;
      case 'Status': {
        const to = variant(p['to']), kind = String(p['kind']), n = num(p['pulses']);
        // a status after a blow or a mend lands on whoever that named ("It is rooted…")
        const again = parts.length > 0 && area === 'Single';
        const named = `${whom(to)}${area === 'AlliesWithin' && where && to !== 'Caster' ? ` ${where}` : ''}`;
        // "every foe caught is", "everyone caught is": one at a time, as the rules take them
        const who = to === 'Caster' ? 'You are' : again ? 'It is' : `${named.replace(/^./, (c) => c.toUpperCase())} is`;
        const span = n > 0 ? ` for ${plural(n, 'turn')}` : '';
        parts.push(`${who} ${kind.toLowerCase()}${span}: ${statusGloss(kind, num(p['magnitude']))}.`);
        break;
      }
      case 'MoveSelf': if (num(p['tiles']) > 0) parts.push(`Moves you up to ${plural(num(p['tiles']), 'square')}.`); break;
      case 'Teleport': parts.push(`Teleports you up to ${plural(num(p['tiles']), 'square')}, over anything in the way.`); break;
      case 'MoveBonus': parts.push(`Your moves this turn go ${plural(num(p['tiles']), 'square')} further.`); break;
      case 'HazardTiles': parts.push(`Sets the ground ${where} ${String(p['kind']).toLowerCase()} for ${plural(num(p['pulses']), 'turn')}: whoever stands there ${statusGloss(String(p['kind']), num(p['magnitude']))}.`); break;
      case 'Trap': parts.push(`Lays a hidden trap ${where} for ${plural(num(p['pulses']), 'turn')}: the first foe to step on it takes ${num(p['dmg'])} damage and is rooted for ${plural(num(p['root_pulses']), 'turn')}.`); break;
      case 'Revive': parts.push(`Stands a downed ally back up with ${num(p['hp'])} HP.`); break;
      case 'ClearStatus': parts.push(`Clears ${String(p['kind']).toLowerCase()} from ${whoText(variant(p['to']), area)}.`); break;
      // docs/72 IT4: what items cast besides the spells' vocabulary
      case 'Resource': parts.push(`Restores ${num(p['amount'])} class resource to ${whoText(variant(p['to'] ?? 'Allies'), area)}.`); break;
      case 'Cleanse': parts.push(`Clears every harmful status from ${whoText(variant(p['to']), area)}.`); break;
      case 'Reveal': parts.push('Every hidden thing on the field shows itself, and every trap is seen.'); break;
      // docs/104 DR2: the druid shapes the land; no door, fixture, exit, trap or unit is ever grown over
      case 'Grow': {
        const into = String(p['into'] ?? variant(p['into']));
        const at = where ? ` ${where}` : '';
        if (into === 'Thicket') parts.push(`The empty floor${at} grows into thicket: slow going, and cover for whoever stands in it.`);
        else if (into === 'Rough') parts.push(`The empty floor${at} heaves into rough ground (mud or ice as the land makes it): slow going.`);
        else parts.push(`Thicket and rough ground${at} clear to open floor.`);
        break;
      }
      case 'Bloom': parts.push(`Heals every friend ${where || 'near you'} who stands in or beside a thicket for ${num(p['amount'])}.`); break;
      case 'Briar': parts.push(`Deals ${num(p['dmg'])} spell damage to every foe ${where || 'near you'} who stands in or beside a thicket, and it bleeds (1 damage at the end of each turn) for 2 turns.`); break;
      // docs/119: on yourself (Action Surge) or on every friend in reach (Warlord's Command), as well as on one friend (Encore)
      case 'Hasten': {
        const who = area === 'SelfUnit' ? 'Every cooldown of yours is' : area === 'Single' ? 'Every cooldown of the friend is' : `Every cooldown of every friend ${where || 'near you'} is`;
        parts.push(`${who} ${plural(num(p['turns']), 'turn')} shorter: ${area === 'SelfUnit' ? 'your' : 'their'} abilities come back sooner.`);
        break;
      }
      case 'Interact': break;
    }
  }
  if (a.slot === 'Passive') parts.unshift('Always on.');
  const reqs = a.requires.map(requirementText).filter(Boolean);
  if (reqs.length) parts.push(`Only ${reqs.join(', ')}.`);
  // docs/104: rules the sim reads where they apply
  const more = SPECIAL[a.id];
  if (more) parts.push(more);
  if (SONG_IDS.has(a.id)) parts.push('A song: +1 Tempo.');
  return parts.join(' ') || a.name;
}

/** The small print under a tooltip: which slot, the cost, the cooldown, the uses. */
export function abilityTerms(a: ActionDef, resource?: string): string[] {
  const out: string[] = [];
  const bonus = a.slots.includes('B'), action = a.slots.includes(1) || a.slots.includes(2);
  if (bonus && action) out.push('Action or bonus');
  else if (bonus) out.push('Bonus action');
  if (a.cost) out.push(`Costs ${a.cost} ${resource?.toLowerCase() ?? 'resource'}`);
  if (a.cooldown) out.push(`Recovers for ${plural(a.cooldown, 'turn')} after use`);
  if (a.perAdventure) out.push(`${plural(a.perAdventure, 'use')} per adventure`);
  return out;
}
